import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/react';
import { useAuth } from '@clerk/react';
import { isClerkConfigured } from '@/lib/clerkEntitlements';
import { hasAdminAccess } from '@/lib/adminAccess';
import { readAdminSelectedClientMeta, subscribeToAdminSelectedClient } from '@/lib/adminClientContext';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { getSessionEmail, subscribeToSessionEmail, clearSessionEmail } from '@/lib/userSession';
import { isLocalDevAuthBypassEnabled } from '@/lib/localDevAuthBypass';
import { supabase, getSupabaseReady } from '@/lib/supabaseClient';
import { BorderBeam } from './BorderBeam';
import RegionSwitcher from './RegionSwitcher';
import { getRegion, regionHref, usAppHref } from '../lib/region';
import { currentRegionHref } from '../lib/regionLocation';
import { lockBodyScrollMobileNav, unlockBodyScrollMobileNav } from '../utils/bodyScrollLock';

// Mobile nav: `custom` is true when the menu closes because the route changed, so it vanishes at
// once instead of sliding out over the new page.
const MOBILE_NAV_OVERLAY_VARIANTS = {
  hidden: { opacity: 0 },
  shown: { opacity: 1 },
  exit: (instant) => ({ opacity: 0, transition: { duration: instant ? 0 : 0.2 } }),
};
const MOBILE_NAV_DRAWER_VARIANTS = {
  hidden: { x: "100%" },
  shown: { x: 0 },
  exit: (instant) => ({ x: "100%", transition: { type: "tween", duration: instant ? 0 : 0.3 } }),
};

const clerkEnabled = isClerkConfigured();
const SHOW_DASHBOARD_DEADLINE_TICKER = false;

/** Everything the mobile drawer's focus trap may land on. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Cmd/Ctrl/Shift/middle clicks on a nav link: let the browser open a new tab or window. */
const isModifiedClick = (event) =>
  Boolean(event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button != null && event.button !== 0)));

/** Smooth-scroll to a landing-page section, retrying briefly while a lazy chunk mounts it. */
/** No border / ring on nav profile avatar (Clerk UserButton) */
const clerkNavUserButtonAppearance = {
  elements: {
    userButtonBox: "border-0 shadow-none",
    userButtonTrigger:
      "!border-0 !shadow-none !ring-0 !ring-offset-0 outline-none focus:!ring-0 focus-visible:!ring-0",
    userButtonAvatarBox: "!border-0 !shadow-none !ring-0 ring-offset-0",
    userButtonAvatarImage: "!border-0",
  },
};

function ClerkSessionBridge({ onSignedInChange }) {
  const { isSignedIn, isLoaded } = useAuth();
  const ref = React.useRef(onSignedInChange);
  ref.current = onSignedInChange;
  React.useEffect(() => {
    if (!isLoaded) return;
    ref.current(Boolean(isSignedIn));
  }, [isLoaded, isSignedIn]);
  return null;
}

/**
 * Header - Unified navigation bar with backdrop blur and mobile drawer
 * Features: OPTRA logo, navigation links, Waitlist pill button, mobile hamburger menu
 * Fully keyboard accessible with proper ARIA labels and 48px+ tap targets
 * Safe-area aware for notched devices
 * Works across all pages with smart navigation logic
 */
// `variant` is accepted by callers for backwards compatibility but no longer changes the markup.
const Header = ({ className = '', region: regionProp = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Which regional site this header belongs to: "sg", "us", or null outside a region (tests,
  // pre-region URLs). The US site (/us) gets its own nav and links sign-in to the US product;
  // everything else is the Singapore header, unchanged apart from the region switcher.
  const siteRegion = regionProp || getRegion();
  const isUsSite = siteRegion === 'us';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [verifiedEmail, setVerifiedEmail] = React.useState(null);
  const [clerkSignedIn, setClerkSignedIn] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);
  const [dashboardTickerItems, setDashboardTickerItems] = React.useState([]);
  const [adminSelectedClient, setAdminSelectedClient] = React.useState(() => readAdminSelectedClientMeta());
  const MotionDiv = motion.div;
  const headerRef = React.useRef(null);
  const mobileToggleRef = React.useRef(null);
  const drawerRef = React.useRef(null);
  const drawerCloseButtonRef = React.useRef(null);
  const isDashboardRoute = location.pathname === "/dashboard";
  // Any page inside the dashboard area; the admin client-context chip must
  // never replace the Sign In button on marketing pages.
  const isDashboardAreaRoute = location.pathname.startsWith("/dashboard");
  
  // Ensure component is mounted before showing (prevents hydration issues)
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  const smoothNavigate = React.useCallback(
    (target, { replace = false, state = null, scroll = true } = {}) => {
      if (!target) return;

      const isBrowser = typeof window !== "undefined";
      const hasHash = target.includes("#");
      const [rawPath, rawHash] = hasHash ? target.split("#") : [target, ""];
      const normalizedPath =
        rawPath && rawPath.trim().length > 0 ? rawPath : location.pathname;
      const sectionId = rawHash?.trim();
      const previousRoute =
        (isBrowser && window.__optraRoute) ||
        `${location.pathname}${location.search}${location.hash}`;

      // window.location leaves React Router, so the basename is not applied for us.
      const browserTarget =
        siteRegion && target.startsWith('/') ? regionHref(siteRegion, target) : target;

      const runPostNavigationEffects = () => {
        if (!isBrowser) return;

        if (scroll) {
          requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          });
        }

        if (sectionId) {
          const tryScrollToSection = (attempt = 0) => {
            const section = document.getElementById(sectionId);
            if (section) {
              section.scrollIntoView({ behavior: "smooth", block: "start" });
              return;
            }

            if (attempt < 8) {
              setTimeout(() => tryScrollToSection(attempt + 1), 75);
            }
          };

          setTimeout(() => tryScrollToSection(0), 120);
        }
      };

      try {
        navigate(normalizedPath, { replace, state });
        runPostNavigationEffects();

        if (isBrowser) {
          setTimeout(() => {
            const currentRoute =
              window.__optraRoute ||
              `${window.location.pathname}${window.location.search}${window.location.hash}`;

            const sameRoute = currentRoute === previousRoute;
            const sectionMissing = sectionId
              ? !document.getElementById(sectionId)
              : false;

            // For hash/section navigation, avoid hard reload fallback.
            // Reloading can race with app-level scroll effects and "bounce" users away from target sections.
            if (sectionId) {
              if (window.location.hash !== `#${sectionId}`) {
                window.location.hash = sectionId;
              }
              return;
            }

            if (sameRoute || sectionMissing) {
              if (replace) {
                window.location.replace(browserTarget);
              } else {
                window.location.assign(browserTarget);
              }
            } else if (sectionId && window.location.hash !== `#${sectionId}`) {
              window.location.hash = sectionId;
            }
          }, 200);
        }

        return;
      } catch (error) {
        console.error("SPA navigation failed, falling back to full reload", error);
      }

      if (!isBrowser) return;

      if (replace) {
        window.location.replace(browserTarget);
      } else {
        window.location.assign(browserTarget);
      }
    },
    [navigate, location.pathname, location.search, location.hash, siteRegion]
  );

  // Body scroll-lock when mobile menu is open (centralized in bodyScrollLock.js)
  React.useEffect(() => {
    if (isMobileMenuOpen) {
      lockBodyScrollMobileNav();
    } else {
      unlockBodyScrollMobileNav();
    }
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change. It closes instantly (no slide-out over the new page);
  // the backdrop / close button still animate it out.
  const [closeMenuInstantly, setCloseMenuInstantly] = React.useState(false);
  React.useEffect(() => {
    setCloseMenuInstantly(true);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  React.useEffect(() => {
    if (isMobileMenuOpen) setCloseMenuInstantly(false);
  }, [isMobileMenuOpen]);

  // Mobile drawer as a real dialog: focus moves to its Close button on open, Tab cycles inside
  // it, Escape closes it, and focus returns to the hamburger when it closes.
  React.useEffect(() => {
    if (!isMobileMenuOpen || typeof document === 'undefined') return undefined;
    const toggleNode = mobileToggleRef.current;
    const focusTimer = window.setTimeout(() => {
      drawerCloseButtonRef.current?.focus();
    }, 60);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMobileMenuOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const root = drawerRef.current;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (node) => node.getAttribute('aria-hidden') !== 'true'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !root.contains(active);
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      toggleNode?.focus();
    };
  }, [isMobileMenuOpen]);

  React.useEffect(() => {
    const email = getSessionEmail();
    setVerifiedEmail(email);
    const unsubscribe = subscribeToSessionEmail((updatedEmail) => {
      setVerifiedEmail(updatedEmail);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    const refresh = () => setAdminSelectedClient(readAdminSelectedClientMeta());
    refresh();
    return subscribeToAdminSelectedClient(refresh);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncHeaderHeightVar = () => {
      const node = headerRef.current;
      if (!node) return;
      const measured = Math.round(node.getBoundingClientRect().height || 0);
      if (!measured) return;
      // The var positions page content below the fixed bar; never let a transient
      // expanded state (e.g. the open mobile drawer) shift the whole page down.
      const capped = Math.min(measured, 160);
      document.documentElement.style.setProperty("--header-live-h", `${capped}px`);
    };
    syncHeaderHeightVar();
    window.addEventListener("resize", syncHeaderHeightVar);
    return () => {
      window.removeEventListener("resize", syncHeaderHeightVar);
    };
  }, [isMounted, isMobileMenuOpen, location.pathname]);

  // The Singapore deadline ticker (currently switched off) is loaded on demand, only on the
  // Singapore dashboard route, so the service never ships in the marketing or US bundles.
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!SHOW_DASHBOARD_DEADLINE_TICKER) return undefined;
    if (!isDashboardRoute || isUsSite) return undefined;
    let alive = true;

    const loadTicker = async () => {
      try {
        const { getSingaporeBusinessDeadlineTickerItems } = await import(
          '../services/singaporeDeadlineTickerService.js'
        );
        const items = await getSingaporeBusinessDeadlineTickerItems();
        if (alive) setDashboardTickerItems(Array.isArray(items) ? items : []);
      } catch {
        if (alive) setDashboardTickerItems([]);
      }
    };

    loadTicker();
    const intervalId = window.setInterval(loadTicker, 5 * 60 * 1000);
    return () => {
      alive = false;
      window.clearInterval(intervalId);
    };
  }, [isDashboardRoute, isUsSite]);

  const isVerifiedUser = Boolean(verifiedEmail);
  const localDevSessionActive = isLocalDevAuthBypassEnabled();
  const sessionActive = isVerifiedUser || (clerkEnabled && clerkSignedIn) || localDevSessionActive;
  const canViewAdmin = hasAdminAccess();

  // Nav items render as real links (`to` for React Router) so they are crawlable and open in a
  // new tab on a modified click; `action` is the in-app behaviour for a plain click.
  // Both regional headers carry one page link (About); Pricing lives on the landing page, the
  // blog and guides are reached from the footer.
  // One About page for the whole site, at the root (outside this region's router): a plain link,
  // a full navigation, the same from both regional headers.
  const usNavItems = React.useMemo(() => [{ label: 'About', id: 'about', href: '/about' }], []);

  const sgNavItems = React.useMemo(() => [{ label: 'About', id: 'about', href: '/about' }], []);

  const navItems = isUsSite ? usNavItems : sgNavItems;

  const isCurrentNavItem = (item) => {
    if (!item.to) return false;
    const [path, hash] = item.to.split('#');
    if (hash) return false;
    return location.pathname === path;
  };

  /** Plain click on a router nav link: close the drawer and run the in-app behaviour. */
  const handleNavLinkClick = (event, item) => {
    if (isModifiedClick(event)) return;
    event.preventDefault();
    setIsMobileMenuOpen(false);
    item.action?.();
  };

  const scrollToTop = () => {
    if (isUsSite) {
      // The US landing lives at "/" (and "/pricing", the same page scrolled to pricing).
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        smoothNavigate('/');
      }
      return;
    }
    if (location.pathname === '/' || location.pathname === '/home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      smoothNavigate('/home');
    }
  };

  const handleNavItemClick = (action) => {
    setIsMobileMenuOpen(false);
    action();
  };

  /** Shared markup for a nav item (desktop bar and mobile drawer): Link, or <a> for Guides. */
  const renderNavItem = (item, { className, style }) => {
    const label = item.label;
    if (item.external) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          style={style}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <span className="inline-flex items-center gap-1">
            {label}
            <ArrowUpRight size={12} strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="sr-only"> (opens in new tab)</span>
        </a>
      );
    }
    if (item.href) {
      // A root page (the one About page): leaves this region's router, same tab.
      return (
        <a href={item.href} className={className} style={style} onClick={() => setIsMobileMenuOpen(false)}>
          <span className="inline-block">{label}</span>
        </a>
      );
    }
    return (
      <Link
        to={item.to}
        className={className}
        style={style}
        aria-current={isCurrentNavItem(item) ? 'page' : undefined}
        onClick={(event) => handleNavLinkClick(event, item)}
      >
        <span className="inline-block">{label}</span>
      </Link>
    );
  };

  const handleBookDemoClick = () => {
    setIsMobileMenuOpen(false);
    if (typeof window === 'undefined') return;
    try {
      const calNs = window.Cal?.ns?.['15min'];
      if (typeof calNs === 'function') {
        calNs('modal', { calLink: 'optralabs/15min' });
        return;
      }
    } catch {
      void 0;
    }
    window.open('https://cal.com/optralabs/15min', '_blank', 'noopener,noreferrer');
  };

  const handleSignOut = React.useCallback(async () => {
    try {
      await getSupabaseReady();
      if (supabase) {
        await supabase.auth.signOut();
      }
    } catch (e) {
      if (import.meta.env?.DEV) {
        console.warn('[Header] Sign out error:', e);
      }
    }
    clearSessionEmail();
    if (typeof document !== 'undefined') {
      document.cookie = 'access_level=; path=/; max-age=0';
      document.cookie = 'plan=; path=/; max-age=0';
      try {
        if (window.localStorage) {
          window.localStorage.removeItem('user_email');
        }
      } catch (err) {
        if (import.meta.env?.DEV) {
          console.warn('[Header] Local storage cleanup error:', err);
        }
      }
    }
    setIsMobileMenuOpen(false);
    smoothNavigate('/home');
  }, [smoothNavigate]);

  // Resolve label and action for Sign in/Sign out nav item
  /** Full URL for post-login return (LoginPage reads `redirect`); avoid looping /login → /login */
  const loginRedirectTarget = React.useMemo(() => {
    if (location.pathname === '/login') return '/dashboard';
    return `${location.pathname}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);

  const goToLoginPage = React.useCallback(() => {
    // Keep login URL clean when a redirect would be redundant.
    // LoginPage already defaults to /dashboard when no redirect is provided.
    const loginPath =
      !loginRedirectTarget ||
      loginRedirectTarget === '/' ||
      loginRedirectTarget === '/home'
        ? '/login'
        : `/login?redirect=${encodeURIComponent(loginRedirectTarget)}`;

    navigate(loginPath);
  }, [navigate, loginRedirectTarget]);

  const loginNavLabel = isVerifiedUser ? 'Sign out' : 'Sign In';
  const loginNavAction = isVerifiedUser ? handleSignOut : goToLoginPage;

  const headerClasses = `w-full ${className}`;

  const navClasses =
    "navbar-blur border-b border-white/20 bg-white/5 backdrop-blur-xl backdrop-saturate-150 px-4 sm:px-6 shadow-md w-full flex items-center h-14 md:h-20 rounded-none";

  const navStyle = {
    backgroundColor: "rgba(127, 149, 240, 0.12)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    minHeight: "var(--nav-h)",
    width: "100%",
    maxWidth: "100%",
    borderRadius: 0,
  };

  const waitlistClasses =
    "cta-gradient text-white px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium shadow-md hover:shadow-lg motion-safe:hover:-translate-y-0.5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4B6FFF] focus-visible:ring-offset-2 flex items-center justify-center leading-none whitespace-nowrap";

  const waitlistStyle = {
    border: "2px solid rgba(110, 168, 255, 0.4)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 16px rgba(75, 111, 255, 0.3)",
  };

  return (
    <>
      {/* The US site never shows Singapore signed-in state, so it does not read the Clerk session. */}
      {clerkEnabled && !isUsSite ? (
        <ClerkSessionBridge onSignedInChange={setClerkSignedIn} />
      ) : null}
      <header
        ref={headerRef}
        role="banner"
        className={headerClasses}
        style={{
          minHeight: 'var(--nav-h)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          width: '100%',
          overflow: 'visible',
          overflowX: 'visible',
          overflowY: 'visible',
          height: 'auto',
          maxHeight: 'none',
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          display: 'block',
          margin: 0,
          marginTop: 0,
          padding: 0,
          paddingTop: 0,
          opacity: isMounted ? 1 : 0,
          transition: 'opacity 0.35s ease-out'
        }}
      >
        <div className="w-full" style={{ maxWidth: '100%' }}>
          <nav className={navClasses} style={navStyle} data-nav="fullwidth">
            <div className="flex items-center justify-between w-full gap-6">
              {/* Left: OPTRA Logo — no border/ring on hover or focus; no lift; click goes to hero/home */}
              <button
                type="button"
                onClick={scrollToTop}
                className="flex items-center justify-center rounded-md p-1 shrink-0 border-0 outline-none shadow-none ring-0 hover:ring-0 hover:border-0 hover:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-0 focus:shadow-none active:ring-0 active:border-0"
                style={{ transition: 'none', transform: 'none', border: 'none', boxShadow: 'none' }}
                aria-label="OPTRA - Go to homepage"
              >
                <img 
                  src="/optra-logo.svg" 
                  alt="OPTRA" 
                  className="h-7 w-7 md:h-12 md:w-12 max-h-[56px] max-w-[56px] pointer-events-none"
                  style={{
                    imageRendering: '-webkit-optimize-contrast',
                    transition: 'none',
                  }}
                />
              </button>

              {/* Mobile menu toggle only (hidden from md breakpoint up) */}
              <button
                type="button"
                ref={mobileToggleRef}
                data-mobile-nav-toggle
                onClick={() => setIsMobileMenuOpen(true)}
                className="shrink-0 items-center justify-center p-1.5 rounded-md text-[#60A5FA] hover:bg-blue-50 focus-ring min-w-[34px] min-h-[34px]"
                aria-label="Open menu"
                aria-haspopup="dialog"
                // The drawer only exists in the DOM while open, so the reference is set then.
                aria-controls={isMobileMenuOpen ? 'mobile-nav' : undefined}
                aria-expanded={isMobileMenuOpen}
              >
                <Menu size={18} />
              </button>

              {/* Right cluster: Desktop Navigation Links + controls */}
              <div className="hidden md:flex flex-1 items-center justify-end whitespace-nowrap gap-2 lg:gap-3">
                {/* Center nav links */}
                <div className="flex items-center gap-3 lg:gap-4 mr-1">
                  {navItems.map((item) => {
                    const isWorkflowNav = item.id === 'workflow';

                    // Hide Dashboard/Solutions for users who are not signed in
                    if (isWorkflowNav && !sessionActive) {
                      return null;
                    }

                    return (
                      <React.Fragment key={item.label}>
                        {renderNavItem(item, {
                          className:
                            'px-1 py-2 whitespace-nowrap text-sm lg:text-[15px] transition-colors duration-200 relative bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent border-0 outline-none shadow-none hover:shadow-none focus:shadow-none active:shadow-none',
                          style: {
                            fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            letterSpacing: '0.003em',
                            fontWeight: 300,
                            color: '#60A5FA',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            textDecoration: 'none',
                          },
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Right: Verified + Login + Speak With Us */}
                <div className="hidden sm:flex items-center shrink-0 gap-2">
                {/* Region toggle sits right after the page links (About), before the auth controls. */}
                <RegionSwitcher region={siteRegion} />
                {isUsSite ? (
                  <a
                    href={usAppHref('/login')}
                    className="text-sm lg:text-[15px] font-semibold whitespace-nowrap"
                    style={{
                      fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      letterSpacing: '0.003em',
                      fontWeight: 600,
                      color: '#60A5FA',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      textDecoration: 'none'
                    }}
                    aria-label="Sign In"
                  >
                    Sign In
                  </a>
                ) : clerkEnabled ? (
                  <div className="flex items-center gap-2 shrink-0">
                    {canViewAdmin && isDashboardAreaRoute && adminSelectedClient?.email ? (
                      <div
                        className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium"
                        style={{
                          borderColor: "rgba(147, 197, 253, 0.55)",
                          backgroundColor: "rgba(239, 246, 255, 0.92)",
                          color: "#1D4ED8",
                        }}
                        title={`Viewing as ${adminSelectedClient.label || adminSelectedClient.email}`}
                      >
                        <span
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold"
                          style={{
                            backgroundColor: "rgba(96, 165, 250, 0.16)",
                            color: "#2563EB",
                          }}
                        >
                          {(String(adminSelectedClient.label || adminSelectedClient.email || "user").slice(0, 2) || "US").toUpperCase()}
                        </span>
                        <span className="max-w-[180px] truncate">
                          {adminSelectedClient.label || adminSelectedClient.email}
                        </span>
                      </div>
                    ) : canViewAdmin && isDashboardAreaRoute ? (
                      <div
                        className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium"
                        style={{
                          borderColor: "rgba(229, 231, 235, 1)",
                          backgroundColor: "rgba(255, 255, 255, 0.92)",
                          color: "#6B7280",
                        }}
                        title="No client selected"
                      >
                        <span
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold"
                          style={{
                            backgroundColor: "rgba(229, 231, 235, 0.9)",
                            color: "#6B7280",
                          }}
                        >
                          --
                        </span>
                        <span className="max-w-[180px] truncate">
                          No client selected
                        </span>
                      </div>
                    ) : !clerkSignedIn ? (
                      <button
                        type="button"
                        onClick={goToLoginPage}
                        className="text-sm lg:text-[15px] font-semibold whitespace-nowrap"
                        style={{
                          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                          letterSpacing: '0.003em',
                          fontWeight: 600,
                          color: '#60A5FA',
                          backgroundColor: 'transparent',
                          boxShadow: 'none'
                        }}
                        aria-label="Sign In"
                      >
                        Sign In
                      </button>
                    ) : (
                      <span className="clerk-nav-user-button inline-flex">
                        <UserButton
                          // Clerk navigates with full URLs, outside React Router: "/" is the
                          // region chooser, so send people to this region's home instead.
                          afterSignOutUrl={currentRegionHref("/")}
                          appearance={clerkNavUserButtonAppearance}
                        />
                      </span>
                    )}
                  </div>
                ) : (
                <button
                  type="button"
                  onClick={loginNavAction}
                  className="text-sm lg:text-[15px] font-semibold whitespace-nowrap"
                  style={{
                    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    letterSpacing: '0.003em',
                    fontWeight: 600,
                    color: '#60A5FA',
                    backgroundColor: 'transparent',
                    boxShadow: 'none'
                  }}
                  aria-label={isVerifiedUser ? 'Sign out' : 'Navigate to Sign in'}
                >
                  {loginNavLabel}
                </button>
                )}
                <button
                  onClick={handleBookDemoClick}
                  className={`${waitlistClasses} hidden sm:flex shrink-0 whitespace-nowrap`}
                  style={{ 
                    ...waitlistStyle, 
                    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    letterSpacing: '0.003em',
                    fontWeight: 300,
                    color: '#60A5FA'
                  }}
                  aria-label="Speak With Us"
                >
                  Speak With Us
                </button>
              </div>
            </div>
            </div>
          </nav>
        </div>
        {SHOW_DASHBOARD_DEADLINE_TICKER && isDashboardRoute && dashboardTickerItems.length > 0 ? (
          <div className="w-full" data-testid="dashboard-deadline-ticker">
            <style>{`
              @keyframes header-dashboard-deadline-scroll-left {
                0% { transform: translateX(0); }
                100% { transform: translateX(-12.5%); }
              }
              @keyframes header-dashboard-deadline-pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.3; }
              }
            `}</style>
            <div
              className="relative overflow-hidden w-full"
              style={{
                background: "#EFF2FD",
                borderTop: "0.5px solid #C8D4F9",
                borderBottom: "0.5px solid #C8D4F9",
                height: "44px",
                display: "flex",
                alignItems: "center",
                fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontWeight: 300,
              }}
            >
              <div
                className="absolute left-0 top-0 z-[2] h-full flex items-center whitespace-nowrap"
                style={{
                  background: "#6FA8FA",
                  color: "#FFFFFF",
                  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontSize: "11px",
                  fontWeight: 300,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "0 14px",
                  gap: "6px",
                }}
              >
                <span
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "9999px",
                    background: "#FFFFFF",
                    animation: "header-dashboard-deadline-pulse 1.2s ease-in-out infinite",
                  }}
                />
                Deadlines
                <span
                  aria-hidden
                  className="absolute top-0"
                  style={{
                    right: "-10px",
                    height: "100%",
                    width: "10px",
                    background: "linear-gradient(to right, #6FA8FA, transparent)",
                  }}
                />
              </div>

              <div className="relative overflow-hidden w-full">
                <div
                  className="flex items-center whitespace-nowrap w-max"
                  style={{
                    animation: "header-dashboard-deadline-scroll-left 28s linear infinite",
                    paddingLeft: "120px",
                  }}
                >
                  {Array.from({ length: 8 })
                    .flatMap((_, cycle) => dashboardTickerItems.map((item, idx) => ({ item, idx, cycle })))
                    .map(({ item, idx, cycle }) => {
                      const countdownDays = Math.max(0, item.countdown_days ?? 0);
                      const tagType = countdownDays <= 14 ? "urgent" : countdownDays <= 45 ? "soon" : "normal";
                      const tagText = item.type === "opens_on"
                        ? (countdownDays <= 14 ? "Opening Soon" : "Upcoming")
                        : (countdownDays <= 14 ? "Closing Soon" : countdownDays <= 45 ? "Active" : "Open");
                      const tagStyleByType = {
                        urgent: {
                          background: "rgba(111, 168, 250, 0.2)",
                          color: "#3A7DE0",
                          border: "0.5px solid rgba(111, 168, 250, 0.5)",
                        },
                        soon: {
                          background: "rgba(244, 162, 97, 0.15)",
                          color: "#C07A30",
                          border: "0.5px solid rgba(244, 162, 97, 0.4)",
                        },
                        normal: {
                          background: "rgba(46, 196, 182, 0.12)",
                          color: "#1A8C82",
                          border: "0.5px solid rgba(46, 196, 182, 0.3)",
                        },
                      };
                      const dotColorByType = {
                        urgent: "#6FA8FA",
                        soon: "#F4A261",
                        normal: "#2EC4B6",
                      };

                      return (
                        <React.Fragment key={`header-deadline-item-${item.id || idx}-${cycle}-${idx}`}>
                          <div
                            className="inline-flex items-center"
                            style={{
                              gap: "10px",
                              marginRight: "48px",
                              fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                              fontSize: "12px",
                              fontWeight: 300,
                              color: "#4A6FA5",
                              letterSpacing: "0.02em",
                            }}
                          >
                            <span className="w-[5px] h-[5px] rounded-full shrink-0" style={{ background: dotColorByType[tagType] }} />
                            <span style={{ color: "#1E3A6E", fontWeight: 300 }}>{item.title}</span>
                            {item.type === "opens_on" ? <span style={{ color: "#6B8CBE" }}>Opens In {countdownDays} Days</span> : null}
                            {item.type === "hard_deadline" ? <span style={{ color: "#6B8CBE" }}>Closes In {countdownDays} Days</span> : null}
                            {item.type === "application_window" ? <span style={{ color: "#6B8CBE" }}>Open Until {item.closes_at || "TBC"}</span> : null}
                            {item.type === "effective_date" ? <span style={{ color: "#6B8CBE" }}>Effective {item.effective_at || "TBC"}</span> : null}
                            {(item.type === "hard_deadline" || item.type === "opens_on") ? (
                              <span
                                style={{
                                  fontSize: "10px",
                                  padding: "2px 7px",
                                  borderRadius: "3px",
                                  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                                  fontWeight: 300,
                                  letterSpacing: "0.06em",
                                  textTransform: "uppercase",
                                  ...tagStyleByType[tagType],
                                }}
                              >
                                {tagText}
                              </span>
                            ) : null}
                          </div>
                          <span
                            aria-hidden
                            style={{
                              color: "#B8C8E8",
                              marginRight: "48px",
                              fontSize: "16px",
                            }}
                          >
                            ·
                          </span>
                        </React.Fragment>
                      );
                    })}
                </div>
              </div>
              <div
                className="absolute right-0 top-0 h-full pointer-events-none z-[1]"
                style={{
                  width: "60px",
                  background: "linear-gradient(to right, transparent, #EFF2FD)",
                }}
              />
            </div>
          </div>
        ) : null}
      </header>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence custom={closeMenuInstantly}>
        {isMobileMenuOpen && (
          <MotionDiv
            className="fixed inset-0 z-[9998] md:hidden"
            custom={closeMenuInstantly}
            variants={MOBILE_NAV_OVERLAY_VARIANTS}
            initial="hidden"
            animate="shown"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Drawer */}
            <MotionDiv
              className="absolute top-0 right-0 bottom-0 w-full max-w-[22rem] sm:max-w-sm bg-white shadow-2xl full-height"
              custom={closeMenuInstantly}
              variants={MOBILE_NAV_DRAWER_VARIANTS}
              initial="hidden"
              animate="shown"
              exit="exit"
              transition={{ type: 'tween', duration: 0.3 }}
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation menu"
              data-drawer="mobile-nav"
              id="mobile-nav"
              ref={drawerRef}
            >
              <div className="flex flex-col h-full safe-padded">
                {/* Drawer Header */}
                <div className="flex items-center justify-between py-4 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      scrollToTop();
                    }}
                    className="flex items-center justify-center px-2 py-1 rounded-md border-0 outline-none shadow-none ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0"
                    style={{ transition: 'none' }}
                    aria-label="OPTRA - Go to homepage"
                  >
                    <img 
                      src="/optra-logo.svg" 
                      alt="OPTRA" 
                      className="h-12 w-12 pointer-events-none"
                      style={{ transition: 'none', imageRendering: '-webkit-optimize-contrast' }}
                    />
                  </button>
                  <button
                    type="button"
                    ref={drawerCloseButtonRef}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-gray-900 hover:text-gray-700 p-3 rounded-md focus-ring min-w-[48px] min-h-[48px] flex items-center justify-center"
                    aria-label="Close menu"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 py-6 overflow-y-auto" data-mobile-menu>
                  <ul className="space-y-1">
                    {navItems.map((item) => {
                      const isWorkflowNav = item.id === 'workflow';

                      // Hide Dashboard/Solutions for users who are not signed in
                      if (isWorkflowNav && !sessionActive) {
                        return null;
                      }

                      return (
                        <li key={item.label}>
                          {renderNavItem(item, {
                            className:
                              'w-full text-left px-4 py-3 text-base rounded-lg transition-colors duration-200 focus-ring min-h-[52px] relative',
                            style: {
                              fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                              letterSpacing: '0.003em',
                              fontWeight: 300,
                              color: '#60A5FA',
                              backgroundColor: 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              minHeight: '52px',
                              padding: '12px 16px',
                              textDecoration: 'none',
                            },
                          })}
                        </li>
                      );
                    })}
                  </ul>

                  {/* Mobile auth CTA */}
                  <div className="mt-3 px-4">
                    {isUsSite ? (
                    <a
                      href={usAppHref('/login')}
                      className="w-full text-left px-4 py-3 text-base rounded-lg transition-transform transition-colors duration-200 hover:-translate-y-0.5 focus-ring min-h-[52px] font-medium"
                      style={{
                        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        letterSpacing: '0.003em',
                        fontWeight: 500,
                        color: '#60A5FA',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        textDecoration: 'none'
                      }}
                      aria-label="Navigate to Sign in"
                    >
                      Sign In
                    </a>
                    ) : (
                    <button
                      type="button"
                      onClick={() => handleNavItemClick(clerkEnabled ? goToLoginPage : loginNavAction)}
                      className="w-full text-left px-4 py-3 text-base rounded-lg transition-transform transition-colors duration-200 hover:-translate-y-0.5 focus-ring min-h-[52px] font-medium"
                      style={{
                        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        letterSpacing: '0.003em',
                        fontWeight: 500,
                        color: '#60A5FA',
                        backgroundColor: 'transparent'
                      }}
                      aria-label={clerkEnabled ? 'Navigate to Sign in' : (isVerifiedUser ? 'Sign out' : 'Navigate to Sign in')}
                    >
                      {clerkEnabled ? 'Sign In' : loginNavLabel}
                    </button>
                    )}
                  </div>

                  {/* Region indicator + switcher */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <RegionSwitcher region={siteRegion} variant="list" />
                  </div>
                </nav>

                {/* Drawer Footer with CTA */}
                <div className="py-5 border-t border-gray-200 safe-padded-bottom mt-auto">
                  <div className="px-4">
                    <button
                      onClick={handleBookDemoClick}
                      className="w-full px-6 py-3.5 rounded-lg text-base font-medium hover:shadow-lg transition-all duration-300 focus-ring min-h-[52px]"
                      style={{ color: '#FFFFFF', backgroundColor: '#60A5FA' }}
                    >
                      Speak With Us
                    </button>
                  </div>
                </div>
              </div>
            </MotionDiv>
          </MotionDiv>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
